import { DOCUMENT } from '@angular/common';
import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import {
  SEO_READY_META,
  SeoHead,
  buildCanonical,
  buildDefaultSeo,
  createSeoContext,
  routeRobots,
  safeJsonForScript,
  safeSocialImage,
} from '../../../seo/seo-generator';

export type OpenGraphPayload = {
  title?: string;
  description?: string;
  url?: string;
  image?: string;
  type?: string;
};

export type TwitterPayload = {
  card?: 'summary' | 'summary_large_image';
  title?: string;
  description?: string;
  image?: string;
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly renderer: Renderer2;
  private routeUrl = '/';

  constructor(
    private readonly meta: Meta,
    private readonly title: Title,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  // Runs before each component, including query-only navigation. Clears metadata
  // from the preceding SPA page; components then refine this complete baseline.
  resetForRoute(url: string): void {
    this.routeUrl = url;
    this.document.head
      ?.querySelectorAll('script[type="application/ld+json"]')
      .forEach((script) => script.remove());
    this.document.head
      ?.querySelectorAll('meta[property^="og:image:"]')
      .forEach((tag) => tag.remove());
    this.applySeo(
      buildDefaultSeo(
        createSeoContext(url, environment.siteBaseUrl, environment.apiBaseUrl)
      )
    );
  }

  applySeo(seo: SeoHead): void {
    this.setTitle(seo.title);
    this.setDescription(seo.description);
    this.setCanonical(seo.canonical);
    this.setRobots(seo.robots || 'index,follow');
    this.meta.updateTag({ name: 'keywords', content: seo.keywords || '' });
    this.setOpenGraph(seo.og);
    if (seo.og.image) {
      this.setOpenGraphImage(seo.og.image, {
        width: Number(seo.og.imageWidth) || undefined,
        height: Number(seo.og.imageHeight) || undefined,
      });
    }
    this.setTwitter(seo.twitter);
    if (seo.jsonLd) this.setJsonLd(seo.jsonLd, 'event-jsonld');
  }

  setTitle(value: string): void {
    this.title.setTitle(value);
    this.meta.updateTag({ name: 'title', content: value }, "name='title'");
    this.meta.updateTag({ property: 'og:title', content: value });
    this.meta.updateTag({ name: 'twitter:title', content: value });
    this.meta.updateTag({ name: SEO_READY_META, content: 'angular' });
  }

  setDescription(value: string): void {
    if (!value) return;
    this.meta.updateTag(
      { name: 'description', content: value },
      "name='description'"
    );
    this.meta.updateTag({ property: 'og:description', content: value });
    this.meta.updateTag({ name: 'twitter:description', content: value });
  }

  setCanonical(url: string): void {
    if (!url) return;
    const parsed = new URL(url, environment.siteBaseUrl);
    const path = parsed.pathname + parsed.search;
    url = buildCanonical(createSeoContext(path, environment.siteBaseUrl));
    const head = this.document.head;
    if (!head) return;
    const existing = head.querySelector("link[rel='canonical']");
    if (existing) {
      this.renderer.setAttribute(existing, 'href', url);
      return;
    }
    const link = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'rel', 'canonical');
    this.renderer.setAttribute(link, 'href', url);
    this.renderer.appendChild(head, link);
  }

  setRobots(value: string): void {
    if (!value) return;
    const policy = routeRobots(this.routeUrl);
    if (policy.startsWith('noindex')) {
      value =
        policy.endsWith('nofollow') || value.includes('nofollow')
          ? 'noindex,nofollow'
          : 'noindex,follow';
    }
    this.meta.updateTag({ name: 'robots', content: value }, "name='robots'");
  }

  setOpenGraph(payload: OpenGraphPayload): void {
    if (payload.title)
      this.meta.updateTag(
        { property: 'og:title', content: payload.title },
        "property='og:title'"
      );
    if (payload.description)
      this.meta.updateTag(
        { property: 'og:description', content: payload.description },
        "property='og:description'"
      );
    if (payload.url) {
      const parsed = new URL(payload.url, environment.siteBaseUrl);
      const canonical = buildCanonical(
        createSeoContext(
          parsed.pathname + parsed.search,
          environment.siteBaseUrl
        )
      );
      this.meta.updateTag(
        { property: 'og:url', content: canonical },
        "property='og:url'"
      );
    }
    if (payload.image) this.setOpenGraphImage(payload.image);
    this.meta.updateTag(
      { property: 'og:type', content: payload.type || 'website' },
      "property='og:type'"
    );
  }

  setTwitter(payload: TwitterPayload): void {
    const card: 'summary' | 'summary_large_image' =
      payload.card === 'summary' || payload.card === 'summary_large_image'
        ? payload.card
        : 'summary_large_image';
    this.setTwitterCard(card);
    if (payload.title)
      this.meta.updateTag(
        { name: 'twitter:title', content: payload.title },
        "name='twitter:title'"
      );
    if (payload.description)
      this.meta.updateTag(
        { name: 'twitter:description', content: payload.description },
        "name='twitter:description'"
      );
    if (payload.image) this.setTwitterImage(payload.image);
  }

  setOpenGraphImage(
    url: string,
    opts?: { width?: number; height?: number; type?: string }
  ): void {
    url =
      safeSocialImage(url, environment.siteBaseUrl) ||
      `${environment.siteBaseUrl}/assets/logo.png`;
    this.meta.updateTag(
      { property: 'og:image', content: url },
      "property='og:image'"
    );
    this.meta.updateTag(
      { property: 'og:image:secure_url', content: url },
      "property='og:image:secure_url'"
    );
    if (opts?.width)
      this.meta.updateTag(
        { property: 'og:image:width', content: String(opts.width) },
        "property='og:image:width'"
      );
    if (opts?.height)
      this.meta.updateTag(
        { property: 'og:image:height', content: String(opts.height) },
        "property='og:image:height'"
      );
    if (opts?.type)
      this.meta.updateTag(
        { property: 'og:image:type', content: opts.type },
        "property='og:image:type'"
      );
  }

  setTwitterImage(url: string): void {
    url =
      safeSocialImage(url, environment.siteBaseUrl) ||
      `${environment.siteBaseUrl}/assets/logo.png`;
    this.meta.updateTag(
      { name: 'twitter:image', content: url },
      "name='twitter:image'"
    );
  }

  setTwitterCard(card: 'summary' | 'summary_large_image'): void {
    this.meta.updateTag(
      { name: 'twitter:card', content: card },
      "name='twitter:card'"
    );
  }

  setJsonLd(schema: object, id = 'jsonld'): void {
    if (!schema) return;
    const head = this.document.head;
    if (!head) return;
    this.clearJsonLd(id);
    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'type', 'application/ld+json');
    this.renderer.setAttribute(script, 'id', id);
    this.renderer.setProperty(script, 'textContent', safeJsonForScript(schema));
    this.renderer.appendChild(head, script);
  }

  clearJsonLd(id = 'jsonld'): void {
    const head = this.document.head;
    if (!head) return;
    const existing = head.querySelector(`#${id}`);
    if (existing) {
      this.renderer.removeChild(head, existing);
    }
  }
}
